import React from "react";
import axios from "axios";

import { apiClient } from "../../api/apiClient";
import { useQuery } from "@tanstack/react-query";
import {
  MRT_ColumnFiltersState,
  MRT_PaginationState,
  MRT_SortingState,
} from "material-react-table";
import {
  type SensorsQueryResult,
  type Sensor,
  SensorsTableUpdateArg,
} from "../../types/sensors";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "react-toastify";

export function useCreateSensor({ table }: SensorsTableUpdateArg) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (sensor: Sensor) => {
      try {
        const response = await apiClient.post("/api/sensor/", sensor);

        table.setCreatingRow(null);
        toast.success(`Sensor ${sensor.name} created successfully`);
        return response.data;
      } catch (error) {
        if (axios.isAxiosError(error) && error.response) {
          const errorData = error.response.data;
          for (const key in errorData) {
            if (Array.isArray(errorData[key])) {
              const errorMessage = errorData[key].join(", ");
              toast.error(`${key.toUpperCase()}: ${errorMessage}`);
            } else {
              toast.error(`${key.toUpperCase()}: ${errorData[key]}`);
            }
          }
        } else {
          toast.error(`Error creating Sensor: ${error}`);
        }
      }
    },
    onSuccess: (newSensor: Sensor) => {
      queryClient.setQueryData<Sensor[]>(["sensor"], (preSensors = []) => [
        ...preSensors,
        newSensor,
      ]);
    },
    onSettled: () => queryClient.invalidateQueries({ queryKey: ["sensor"] }),
  });
}

export function useDeleteSensor() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (sensorId: string) => {
      try {
        await apiClient.delete(`/api/sensor/${sensorId}`);
        toast.success("Sensor successfully deleted");
      } catch (error) {
        toast.error("Failed to delete the Sensor");
      }
    },
    onMutate: (sensorId: string) => {
      queryClient.setQueryData(["sensor"], (prevSensors: any) =>
        prevSensors?.filter((sensor: Sensor) => sensor.id !== sensorId)
      );
    },
    onSettled: () => queryClient.invalidateQueries({ queryKey: ["sensor"] }),
  });
}

export function useSearchSensors({ q }: { q: string }) {
  return useQuery<SensorsQueryResult[]>({
    queryKey: ["sensor", q],
    queryFn: async () => {
      try {
        const url = `/api/sensor?search=${q}`;

        const response = await apiClient.get(url);

        return [
          {
            sensors: response.data.results as Sensor[],
            rowCount: response.data.count,
          },
        ];
      } catch (error) {
        //
        return [{ sensors: [], rowCount: 0 }];
      }
    },
    refetchOnWindowFocus: true,
  });
}

export function useGetAllSensors() {
  return useQuery<SensorsQueryResult[]>({
    queryKey: ["all_sensors"],
    queryFn: async () => {
      try {
        const url = `/api/sensor`;

        const response = await apiClient.get(url);
        return [
          {
            sensors: response.data.results as Sensor[],
            rowCount: response.data.count,
          },
        ];
      } catch (error) {
        //
        return [{ sensors: [], rowCount: 0 }];
      }
    },
    refetchOnWindowFocus: false,
  });
}

export function useGetSensor({
  pagination,
  columnFilters,
  sorting,
  globalFilter,
  exportCsv,
}: {
  pagination: MRT_PaginationState;
  columnFilters: MRT_ColumnFiltersState;
  sorting: MRT_SortingState;
  globalFilter: string | undefined;
  exportCsv?: boolean;
}) {
  return useQuery<SensorsQueryResult[]>({
    queryKey: [
      "sensor",
      pagination,
      columnFilters,
      sorting,
      globalFilter,
      exportCsv,
    ],
    queryFn: async () => {
      try {
        const url = `/api/sensor`;
        const params = new URLSearchParams({
          page: (pagination.pageIndex + 1).toString(),
          limit: pagination.pageSize.toString(),
          filters: JSON.stringify(columnFilters),
          sorting: JSON.stringify(sorting),
          globalFilter: globalFilter ?? "",
        });

        const response = await apiClient.get(url, { params });

        return [
          {
            sensors: response.data.results as Sensor[],
            rowCount: response.data.count,
          },
        ];
      } catch (error) {
        //
        return [{ sensors: [], rowCount: 0 }];
      }
    },
    refetchOnWindowFocus: true,
  });
}

export function useGetSensorDetail({ name }: { name: string }) {
  return useQuery<Sensor[]>({
    queryKey: ["sensor"],
    queryFn: async () => {
      try {
        const url = `/api/Sensor`;
        const params = new URLSearchParams({
          name: name,
        });

        const response = await apiClient.get(url, { params });

        return response.data.results;
      } catch (error) {
        //
        return {};
      }
    },
    refetchOnWindowFocus: true,
  });
}

export function useUpdateSensor({ table }: SensorsTableUpdateArg) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (sensor: Sensor) => {
      try {
        await apiClient.patch(`/api/sensor/${sensor.id}/`, sensor);

        table.setEditingRow(null);
        toast.success(`Sensor ${sensor.name} updated successfully`);
      } catch (error) {
        if (axios.isAxiosError(error) && error.response) {
          const errorData = error.response.data;
          for (const key in errorData) {
            if (Array.isArray(errorData[key])) {
              const errorMessage = errorData[key].join(", ");
              toast.error(`${key.toUpperCase()}: ${errorMessage}`);
            } else {
              toast.error(`${key.toUpperCase()}: ${errorData[key]}`);
            }
          }
        } else {
          toast.error(`Error updating Sensor ${error}`);
        }
      }
    },

    onMutate: (newSensorInfo: Sensor) => {
      queryClient.setQueryData(["Sensor"], (prevSensors: any) =>
        prevSensors?.map((prevSensor: Sensor) =>
          prevSensor.id === newSensorInfo.id ? newSensorInfo : prevSensor
        )
      );
    },
    onSettled: () => queryClient.invalidateQueries({ queryKey: ["Sensor"] }),
  });
}

export const useExportSensors = (columnFilters: any, sorting: any) => {
  const exportSensors = async () => {
    try {
      const response = await apiClient.post("/api/sensor/export/", {
        params: {
          filters: JSON.stringify(columnFilters),
          sorting: JSON.stringify(sorting),
        },
        responseType: "blob",
      });

      const blob = new Blob([response.data], { type: "text/csv" });
      const downloadUrl = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = downloadUrl;

      const now = new Date();
      const dateString = now.toISOString().split("T")[0];
      const timestamp = now.getTime();
      const filename = `Sensors-${dateString}-${timestamp}.csv`;

      a.download = filename;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(downloadUrl);
      a.remove();

      toast.success(`${filename} downloaded successfully`);
    } catch (error) {
      toast.error(`Failed to download file : ${error}`);
    }
  };

  return exportSensors;
};

export const useDeleteSensors = (
  columnFilters: any,
  sorting: any,
  rowCount: number
) => {
  const deleteSensors = async () => {
    try {
      await apiClient.post("/api/sensor/delete/", {
        params: {
          filters: JSON.stringify(columnFilters),
          sorting: JSON.stringify(sorting),
        },
      });

      toast.success(`Deleted ${rowCount} rows successfully`);
    } catch (error) {
      toast.error("Failed to delete rows");
    } finally {
      setTimeout(() => {
        window.location.reload();
      }, 1000);
    }
  };

  return deleteSensors;
};

export const useApplySensor = () => {
  const applySensor = async () => {
    try {
      await apiClient.get("/api/Sensor/applyall/", {
        params: {},
      });

      toast.success(`Applied successfully`);
    } catch (error) {
      toast.error("Failed to apply");
    } finally {
      /* setTimeout(() => {
                 window.location.reload();
             }, 1000); */
    }
  };

  return applySensor();
};

export const usePollSensors = () => {
  const pollSensors = async () => {
    try {
      await apiClient.get("/api/sensor/poll/", {
        params: {},
      });

      toast.success(`Polled successfully`);
    } catch (error) {
      toast.error("Failed to poll");
    } finally {
      /* setTimeout(() => {
                 window.location.reload();
             }, 1000); */
    }
  };

  return pollSensors();
};
